import { Link } from 'react-router-dom';
import Lottie from 'lottie-react';
import Footer from '../home/Footer';
import HeroSection from '../home/HeroSection';
import CaseStudiesSection from '../home/CaseStudiesSection';
import TestimonialsSection from '../home/TestimonialsSection';
import { normalizeCaseStudyCategories, ensureCardsHaveCategoryKeys, buildCaseStudyListFilters } from '../../content/caseStudyCategories';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { buildMobileStyleSheet } from '../services/editable/styleUtils';
import { useEditablePage } from '../services/editable/useEditablePage';
import CaseStudiesEditor from '../../admin/editors/CaseStudiesEditor';
import TestimonialsEditor from '../../admin/editors/TestimonialsEditor';
import caseStudyAnimation from '../../assets/gif/case-study.json';
import arrowIcon from '../../assets/rightarrow.svg';
import './caseStudiesListingLayout.css';
import '../home/NumbersStatsSection.css';
import '../home/MeetingBannerSection.css';
import '../services/editable/editable.css';

/**
 * Generic, CMS-driven Case Studies LISTING page editor — admin-only
 * counterpart to EditableHomePage.jsx. The live /case-studies route keeps
 * using its own caseStudies.jsx, now reading hero/proof/CTA-banner text from
 * the same `caseStudiesPage` CMS data this editor writes to.
 *
 * Cards/categories/stats/detail-pages already have full CRUD via the
 * existing CaseStudiesEditor admin panel — embedded directly here (not
 * duplicated) so nothing is only "editable elsewhere". Testimonials (shared
 * with the Home page — same underlying items/heading, no page-specific copy)
 * are likewise embedded via the existing TestimonialsEditor, with the
 * heading text edited inline here through the Home page's own
 * `testimonialsHeading` field.
 */
export default function EditableCaseStudiesPage({ data, styles, onChange, preview = false, fullContent, onFullContentChange }) {
  const isEditing = !preview;

  const statsRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const ctaRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });

  /* Sourced directly from the local editing draft (fullContent), not the
     global site-content hooks — those only catch up ~700ms later once the
     debounced autosave completes, which would otherwise make inline edits
     here look like they "revert" for a moment. */
  const stats = fullContent?.caseStudies?.stats || [];
  const categories = normalizeCaseStudyCategories(fullContent?.caseStudies?.categories || []);
  const cards = ensureCardsHaveCategoryKeys(fullContent?.caseStudies?.cards || [], categories);
  const filterOptions = buildCaseStudyListFilters(categories);

  const { T, SectionToggle, showSection, isSectionHidden, LinkPick } = useEditablePage({
    data,
    styles,
    onChange,
    isEditing,
  });

  const homePageData = fullContent?.homePage?.data;
  const homePageHelpers = useEditablePage({
    data: homePageData,
    styles: fullContent?.homePage?.styles,
    onChange: (patch) => onFullContentChange?.({ ...fullContent, homePage: { ...fullContent.homePage, ...patch } }),
    isEditing,
  });

  // caseStudies.stats lives in a different CMS section (shared with the existing
  // CaseStudiesEditor panel below) — wrap it so the same T()/RemoveBtn/AddBtn
  // primitives can edit it inline here too, routing data changes to
  // fullContent.caseStudies.stats and style changes to this page's own styles.
  const statsHelpers = useEditablePage({
    data: { stats },
    styles,
    onChange: (patch) => {
      if (patch.styles) onChange?.(patch);
      if (patch.data?.stats) {
        onFullContentChange?.({ ...fullContent, caseStudies: { ...fullContent.caseStudies, stats: patch.data.stats } });
      }
    },
    isEditing,
  });

  return (
    <div className="case-studies-page case-studies-page--listing">
      <style>{buildMobileStyleSheet(styles, 640)}</style>

      <div className="cs-listing-stack">
        {showSection('hero') ? (
          <div data-svcpg-hidden={isEditing && isSectionHidden('hero') ? 'true' : undefined}>
            <SectionToggle sectionKey="hero" label="Hero" />
            <HeroSection
              variant="case-studies"
              caseStudiesHeadline={
                <>
                  {T('hero.headlinePrefix', 'hero.headlinePrefix', data.hero.headlinePrefix, 'span')}{' '}
                  <span className="headline-purple">
                    {T('hero.headlineAccent', 'hero.headlineAccent', data.hero.headlineAccent, 'span')}
                  </span>
                </>
              }
              caseStudiesDescription={T('hero.description', 'hero.description', data.hero.description, 'span')}
              caseStudiesVisual={<Lottie className="hero-lottie--case-studies" animationData={caseStudyAnimation} loop autoplay />}
            />
          </div>
        ) : null}

        {showSection('stats') ? (
          <section
            className="numbers-stats-section"
            aria-label="Key metrics"
            ref={statsRef}
            data-svcpg-hidden={isEditing && isSectionHidden('stats') ? 'true' : undefined}
          >
            <SectionToggle sectionKey="stats" label="Numbers / stats" />
            <div className="numbers-stats-section__wrap">
              <div className={`numbers-stats-card${stats.length > 4 ? ' numbers-stats-card--many' : ''}`}>
                {stats.map((item, i) => (
                  <div key={i} className={`numbers-stat${isEditing ? ' svcpg-removable' : ''}`}>
                    {statsHelpers.T(`stats.${i}.value`, `stats.${i}.value`, item.value, 'div', 'numbers-stat__value')}
                    {statsHelpers.T(`stats.${i}.label`, `stats.${i}.label`, item.label, 'div', 'numbers-stat__label')}
                    <statsHelpers.RemoveBtn path="stats" index={i} label="Remove stat" />
                  </div>
                ))}
              </div>
              <statsHelpers.AddBtn path="stats" label="Add stat" template={{ value: '0', label: 'NEW METRIC' }} />
            </div>
          </section>
        ) : null}

        {showSection('proof') ? (
          <div data-svcpg-hidden={isEditing && isSectionHidden('proof') ? 'true' : undefined}>
            <SectionToggle sectionKey="proof" label="Proof / case study grid" />
            <CaseStudiesSection
              sectionId="case-studies-proof-preview"
              containerSurface="white"
              headline={
                <>
                  {T('proof.headlinePrefix', 'proof.headlinePrefix', data.proof.headlinePrefix, 'span')}{' '}
                  <span className="headline-purple">
                    {T('proof.headlineAccent', 'proof.headlineAccent', data.proof.headlineAccent, 'span')}
                  </span>
                </>
              }
              description={T('proof.description', 'proof.description', data.proof.description, 'span')}
              cases={cards}
              filterOptions={filterOptions}
              caseStudyCategories={categories}
              pageSize={4}
              showViewAllButton={false}
            />
            <div className="svcpg-embedded-editor">
              <CaseStudiesEditor content={fullContent} onChange={onFullContentChange} />
            </div>
          </div>
        ) : null}

        {showSection('testimonials') ? (
          <div data-svcpg-hidden={isEditing && isSectionHidden('testimonials') ? 'true' : undefined}>
            <SectionToggle sectionKey="testimonials" label="Testimonials" />
            {homePageData ? (
              <div className="section-container" style={{ paddingBottom: 0 }}>
                <p className="svcpg-hint" style={{ marginBottom: 12 }}>
                  Heading below is shared with the Home page's testimonials section.
                </p>
                <h2 className="section-headline">
                  {homePageHelpers.T(
                    'testimonialsHeading.headingPrefix',
                    'testimonialsHeading.headingPrefix',
                    homePageData.testimonialsHeading.headingPrefix,
                    'span'
                  )}{' '}
                  <span className="headline-purple">
                    {homePageHelpers.T(
                      'testimonialsHeading.headingAccent',
                      'testimonialsHeading.headingAccent',
                      homePageData.testimonialsHeading.headingAccent,
                      'span'
                    )}
                  </span>{' '}
                  {homePageHelpers.T(
                    'testimonialsHeading.headingSuffix',
                    'testimonialsHeading.headingSuffix',
                    homePageData.testimonialsHeading.headingSuffix,
                    'span'
                  )}
                </h2>
                <p className="section-description">
                  {homePageHelpers.T(
                    'testimonialsHeading.description',
                    'testimonialsHeading.description',
                    homePageData.testimonialsHeading.description,
                    'span'
                  )}
                </p>
              </div>
            ) : null}
            <TestimonialsSection sectionId="client-testimonials" />
            <div className="svcpg-embedded-editor">
              <TestimonialsEditor content={fullContent} onChange={onFullContentChange} />
            </div>
          </div>
        ) : null}

        {showSection('ctaBanner') ? (
          <section
            className="meeting-banner-section"
            ref={ctaRef}
            data-svcpg-hidden={isEditing && isSectionHidden('ctaBanner') ? 'true' : undefined}
          >
            <SectionToggle sectionKey="ctaBanner" label="CTA banner" />
            <div className="section-container meeting-banner-section__wrap">
              <div className="meeting-banner">
                <div className="meeting-banner__decor meeting-banner__decor--bl" aria-hidden />
                <div className="meeting-banner__decor meeting-banner__decor--tr" aria-hidden />
                <div className="meeting-banner__inner">
                  <h2 className="meeting-banner__title">{T('ctaBanner.title', 'ctaBanner.title', data.ctaBanner.title, 'span')}</h2>
                  <p className="meeting-banner__sub">{T('ctaBanner.subtitle', 'ctaBanner.subtitle', data.ctaBanner.subtitle, 'span')}</p>
                  <Link to={data.ctaBanner.ctaTo} className="meeting-banner__cta">
                    {T('ctaBanner.ctaLabel', 'ctaBanner.ctaLabel', data.ctaBanner.ctaLabel, 'span', 'meeting-banner__cta-text')}
                    <span className="meeting-banner__cta-arrow" aria-hidden>
                      {LinkPick('ctaBanner.ctaTo', data.ctaBanner.ctaTo, <img src={arrowIcon} alt="" />, 'CTA banner link')}
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </section>
        ) : null}
      </div>

      <Footer />
    </div>
  );
}
